import { useEffect, useRef } from "react";

/**
 * Attach a subtle mouse-driven parallax to a stage element.
 * Moves `imageRef` in the opposite direction of the cursor and `overlayRef`
 * in the same direction, producing a light depth effect on desktop only.
 *
 * The hook is a no-op when:
 *  - `enabled` is false (typically when reduced-motion is requested)
 *  - the device reports coarse pointer (touch)
 */
export default function useHeroParallax({ stageRef, imageRef, overlayRef, enabled = true }) {
  // Keep animation state in refs so the effect body has no reactive dependencies.
  const state = useRef({ tx: 0, ty: 0, cx: 0, cy: 0, rafId: 0 });

  useEffect(() => {
    if (!enabled) return;
    const stage = stageRef.current;
    if (!stage) return;
    if (typeof window === "undefined") return;
    if (window.matchMedia?.("(pointer: coarse)").matches) return;

    const s = state.current;

    const onMove = (e) => {
      const rect = stage.getBoundingClientRect();
      s.tx = (e.clientX - rect.left) / rect.width - 0.5;
      s.ty = (e.clientY - rect.top) / rect.height - 0.5;
    };

    const tick = () => {
      s.cx += (s.tx - s.cx) * 0.06;
      s.cy += (s.ty - s.cy) * 0.06;
      if (imageRef.current) {
        imageRef.current.style.transform =
          `translate3d(${s.cx * -18}px, ${s.cy * -12}px, 0) scale(1.04)`;
      }
      if (overlayRef.current) {
        overlayRef.current.style.transform =
          `translate3d(${s.cx * 22}px, ${s.cy * 18}px, 0)`;
      }
      s.rafId = requestAnimationFrame(tick);
    };

    stage.addEventListener("mousemove", onMove);
    s.rafId = requestAnimationFrame(tick);
    return () => {
      stage.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(s.rafId);
    };
  }, [enabled, stageRef, imageRef, overlayRef]);
}
